/* eslint-disable */
import React, { useCallback, useRef, useState } from 'react';
import { supabase } from '../../supabase';
import { parseRows, REQUIRED_COLS, OPTIONAL_COLS, lookupExcelStage } from './devImportUtils';
import { useClassifyImport } from './hooks/useDevImportClassify';
import { DevImportProgress } from './DevImportProgress';
import { DevImportConflictModal } from './DevImportConflictModal';
import styles from './DevImport.module.css';

const ICON_X = <svg viewBox="0 0 24 24"><path d="M18 6 6 18M6 6l12 12" /></svg>;
const ICON_UPLOAD = <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>;
const ICON_INFO = <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>;

// T-1039 — Underscore-prefixed metadata keys (_stage_excel_raw, _stage_was_valid,
// _stage_was_missing) below are used by DevConflictCard to render Excel context
// + amber "Auto-corrected" chip. They are persisted into developments.data because
// stripping via PostgREST UPDATE would require N+1 fetch-modify-write or a SQL
// function (out of scope). ~3 bytes/row, useful audit trail. (Option B per Brief.)
function buildRawData(row, batchId, user) {
  // T-1039 — STAGE lookup replaces the hardcoded DONE_STAGE/DONE_STATUS pair.
  // Empty / missing / invalid cells fall back to DEFAULT_STAGE_PAIR (DONE);
  // amber chip in conflict card is driven by _stage_was_valid=false.
  const stageLookup = lookupExcelStage(row.stage_excel_raw);
  const out = {
    model: row.item_code,
    dev_number: row.item_code,
    factory: row.factory,
    fty: row.factory,
    designer: row.designer,
    open_date: row.open_date,
    dev_date: row.open_date,
    material: row.material || '',
    season: row.season || '',
    shape: row.shape || '',
    imported: true,
    import_batch_id: batchId,
    stage_status: stageLookup.pair.stage_status,
    full_status: stageLookup.pair.full_status,
    // Per-row metadata so the conflict card can render Excel context + warning chip.
    // Stripped before commit via stripStageMetadata() — see Step 2 commit handler.
    _stage_excel_raw:   stageLookup.original,
    _stage_was_valid:   stageLookup.wasValid,
    _stage_was_missing: !row.has_stage_column,
    created_by: user?.email || '',
    created_by_name: user?.email?.split('@')[0] || '',
  };
  // Honor the Excel "open date" column as the first-stage start timestamp.
  // commit_dev_import reads this as explicit created_at for the developments row.
  if (row.open_date_iso) {
    out.stageStartedAt = row.open_date_iso;
  }
  return out;
}

export function DevImportModal({ onClose, user, showToast }) {
  const [phase, setPhase] = useState('upload'); // upload | parsing | classifying | conflict | done | error
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState([]);
  const [parseError, setParseError] = useState('');
  const [classifyError, setClassifyError] = useState(null);
  const [session, setSession] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const classifyMut = useClassifyImport();

  const startClassification = useCallback(async (rows, bid) => {
    setPhase('classifying');
    setClassifyError(null);
    const sid = (crypto.randomUUID && crypto.randomUUID()) || `session_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const rpcRows = rows.map(r => ({
      row_index: r.row_index,
      ...buildRawData(r, bid, user),
    }));
    try {
      const result = await classifyMut.mutateAsync({ session: sid, rows: rpcRows, uploadedBy: user?.email });
      setSession(sid);
      setSummary(result);
      setPhase('conflict');
    } catch (err) {
      setClassifyError(err);
      setPhase('error');
    }
  }, [classifyMut, user]);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setParseError('');
    setFileName(file.name);
    setPhase('parsing');
    try {
      const XLSX = await import('xlsx');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = parseRows(ws, XLSX);
      if (rows.length === 0) {
        setParseError('No valid rows found. Check that required columns exist and at least one row has an item code.');
        setPhase('upload');
        return;
      }
      setParsed(rows);
      const bid = `import_${Date.now()}`;
      setBatchId(bid);
      // Save batch metadata BEFORE classification (keeps existing DevImportHistory behaviour).
      await supabase.from('dev_import_batches').insert({
        id: bid,
        filename: file.name,
        item_count: rows.length,
        imported_by: user?.email || '',
        created_at: new Date().toISOString(),
      });
      await startClassification(rows, bid);
    } catch (err) {
      setParseError(err.message || 'Failed to parse file');
      setPhase('upload');
    }
  }, [user, startClassification]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleRetryClassify = useCallback(() => {
    if (!parsed.length || !batchId) return;
    startClassification(parsed, batchId);
  }, [parsed, batchId, startClassification]);

  // PHASE: conflict modal (takes over the screen entirely)
  if (phase === 'conflict' && session) {
    return (
      <DevImportConflictModal
        session={session}
        filename={fileName}
        uploadedBy={user?.email}
        batchId={batchId}
        summary={summary}
        showToast={showToast}
        onClose={onClose}
        onCommitted={() => { if (onClose) onClose(); }}
      />
    );
  }

  // PHASE: progress overlay (parsing / classifying / error)
  if (phase === 'parsing' || phase === 'classifying') {
    return <DevImportProgress phase={phase} rowCount={parsed.length} />;
  }

  if (phase === 'error') {
    return <DevImportProgress phase="classifying" rowCount={parsed.length} error={classifyError} onRetry={handleRetryClassify} onClose={onClose} />;
  }

  // PHASE: upload (initial)
  return (
    <div className={`${styles.dna} ${styles.overlay}`} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHead}>
          <div className={styles.modalTitle}>Import developments from Excel</div>
          <button onClick={onClose} className={styles.closeBtn} aria-label="Close">{ICON_X}</button>
        </div>
        <div className={styles.modalBody}>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            className={`${styles.drop} ${dragOver ? styles.dropOver : ''}`}
          >
            <div className={styles.dropIconWrap}>{ICON_UPLOAD}</div>
            <div className={styles.dropTitle}>Drag &amp; drop your Excel file</div>
            <div className={styles.dropHint}>or click to choose · .xlsx</div>
            <button type="button" className={styles.dropBtn}>Choose file</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
          </div>

          <div className={styles.cols}>
            <div className={styles.colsLabel}>Required columns</div>
            <div className={styles.badges}>
              {REQUIRED_COLS.map(c => <span key={c.field} className={`${styles.badge} ${styles.badgeReq}`}>{c.label}</span>)}
            </div>
            <div className={`${styles.colsLabel} ${styles.colsLabelOpt}`}>Optional columns</div>
            <div className={styles.badges}>
              {OPTIONAL_COLS.map(c => <span key={c.field} className={styles.badge}>{c.label}</span>)}
            </div>
          </div>

          {parseError && <div className={styles.errNote}>{parseError}</div>}
          <div className={styles.infoNote}>
            {ICON_INFO}
            <span>Every row is checked against existing developments before anything is written. You'll review and resolve conflicts before any save.</span>
          </div>
        </div>
        <div className={styles.modalFoot} style={{ justifyContent: 'flex-end' }}>
          <button onClick={onClose} className={styles.btnGhost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
